/**
 * Remembers the tenant a user last had active, so a multi-tenant user returns
 * to the same organization instead of landing on "No tenant selected" (#167).
 *
 * This is a convenience hint only. It is always validated against the user's
 * current membership list before use (see fetchCurrentUser), so a remembered id
 * can never scope a user to a tenant they don't belong to — including a
 * different user signing in on the same browser.
 *
 * Kept in its own localStorage key rather than relying on the redux-persist auth
 * blob: that blob is versioned and wiped on migration bumps, whereas the
 * last-used tenant should survive those.
 */

const STORAGE_KEY = 'graphit:last-tenant-id'

/**
 * Persist the active tenant id.
 * @param {string|number|null|undefined} tenantId
 */
export function rememberTenantId(tenantId) {
  if (tenantId == null || tenantId === '') return
  try {
    window.localStorage.setItem(STORAGE_KEY, String(tenantId))
  } catch {
    // Private mode or storage disabled — remembering is best-effort.
  }
}

/**
 * The last remembered tenant id, or null if none/unavailable.
 * @returns {string|null}
 */
export function readRememberedTenantId() {
  try {
    return window.localStorage.getItem(STORAGE_KEY) || null
  } catch {
    return null
  }
}

/**
 * Forget the remembered tenant.
 */
export function clearRememberedTenantId() {
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Nothing to do.
  }
}
